/**
 * Personal edit link of a registration (ADB-169): generate, copy, send by WhatsApp, revoke.
 * The link lets the participant complete their form (and sign) on the public site. It is not
 * the QR token: it expires (7 days) and an admin can revoke it.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Link2, Loader2, MessageCircle, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import type { ParticipantWithDetails } from '@/Services/eventParticipants.service';
import { adminCreateEditLink, adminGetEditLink, adminRevokeEditLinks } from '../public/publicRegistrationRepository';
import { publicEditUrl } from '../public/publicUrls';
import { needsSignature } from '../public/registrationEditLogic';
import { participantPhone } from '../utils/participantPhone';
import { whatsappUrl } from '../utils/whatsapp';

interface EditLinkDialogProps {
  participant: ParticipantWithDetails | null;
  eventName: string;
  signatureMode?: string | null;
  onOpenChange: (open: boolean) => void;
}

export function EditLinkDialog({ participant, eventName, signatureMode, onOpenChange }: EditLinkDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const participantId = participant?.id;
  const queryKey = ['registration_edit_link', participantId];
  const { data: link, isLoading } = useQuery({
    queryKey,
    queryFn: () => adminGetEditLink(participantId as string),
    enabled: !!participantId,
    gcTime: 0,
  });

  const run = async (action: () => Promise<unknown>, okTitle?: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await queryClient.invalidateQueries({ queryKey });
      if (okTitle) toast({ title: okTitle });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo completar la acción');
    } finally {
      setBusy(false);
    }
  };

  const name = participant?.minister?.full_name ?? participant?.guest?.full_name ?? 'Participante';
  const unsigned = participant ? needsSignature(participant, signatureMode) : false;
  const url = link ? publicEditUrl(link.token) : null;
  const expires = link ? new Date(link.expires_at).toLocaleDateString('es-BO') : null;
  const firstName = name.split(' ')[0];
  const message = url
    ? `Hola ${firstName}, ${
        unsigned ? 'falta su firma en' : 'puede completar o corregir los datos de'
      } su inscripción a ${eventName}. Use este enlace personal (vence el ${expires}): ${url}`
    : '';
  const wa = participant && url ? whatsappUrl(participantPhone(participant), message) : null;

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Enlace copiado' });
    } catch {
      toast({ title: 'No se pudo copiar. Seleccione el enlace y cópielo a mano.', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={!!participant} onOpenChange={(open) => { if (!open) { setError(null); onOpenChange(false); } }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{unsigned ? 'Pedir firma' : 'Enlace de edición'} — {name}</DialogTitle>
          <DialogDescription>
            Un enlace personal para que la persona complete o corrija su formulario{unsigned ? ' y firme' : ''} desde su
            teléfono. Vence a los 7 días y usted puede anularlo. Quien tenga el enlace puede editar esta inscripción
            mientras esté vigente: envíelo solo a la persona.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Cargando…
          </p>
        )}

        {!isLoading && !link && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Esta inscripción no tiene un enlace vigente.</p>
            <Button
              onClick={() => participantId && run(() => adminCreateEditLink(participantId), 'Enlace generado')}
              disabled={busy}
            >
              {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Link2 className="mr-2 size-4" />}
              Generar enlace
            </Button>
          </div>
        )}

        {link && url && (
          <div className="space-y-3">
            <Input value={url} readOnly onFocus={(e) => e.currentTarget.select()} />
            <p className="text-xs text-muted-foreground">
              Vence el {expires}
              {link.use_count ? ` · usado ${link.use_count} ${link.use_count === 1 ? 'vez' : 'veces'}` : ' · aún no se usó'}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={copy}>
                <Copy className="mr-2 size-4" /> Copiar
              </Button>
              {wa ? (
                <Button asChild>
                  <a href={wa} target="_blank" rel="noreferrer">
                    <MessageCircle className="mr-2 size-4" /> Enviar por WhatsApp
                  </a>
                </Button>
              ) : (
                <Button disabled title="No hay un celular válido (8 dígitos) para esta persona">
                  <MessageCircle className="mr-2 size-4" /> Sin celular válido
                </Button>
              )}
              <Button
                variant="outline"
                className="text-destructive"
                onClick={() => participantId && run(() => adminRevokeEditLinks(participantId), 'Enlace anulado')}
                disabled={busy}
              >
                <Trash2 className="mr-2 size-4" /> Anular
              </Button>
            </div>
            <Button
              variant="link"
              className="h-auto p-0 text-xs"
              onClick={() => participantId && run(() => adminCreateEditLink(participantId), 'Enlace nuevo generado')}
              disabled={busy}
            >
              Generar uno nuevo (anula este)
            </Button>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
}
